import { from, concat, of } from 'rxjs';
import workflow from '@banking/ui/lib/workflow';
import { GetBankAccountsRequest, GetMerchantBankAccountsRequest } from '@square/dashboard-protos/models/squareup/deposits/bank_account_service';
import { ActionType as AllLocationsActionType, update as allLocationsUpdate, INITIAL_STATE as ALL_LOCATIONS_INITIAL_STATE, initAllLocationsAction } from '@banking/ui/workflows/bank-accounts/all-locations';
import { ActionType as ExternalBankAccountsActionType, update as externalBankAccountsUpdate, INITIAL_STATE as EXTERNAL_BANK_ACCOUNTS_INITIAL_STATE, initExternalBankAccountsAction } from '@banking/ui/workflows/bank-accounts/external-bank-accounts';
import { INITIAL_STATE as CHANGE_BANK_ACCOUNT_INITIAL_STATE, ActionType as ChangeBankAccountActionType, update as changeBankAccountUpdate } from '@banking/ui/workflows/bank-accounts/change-bank-account';
import { INITIAL_STATE as CANCEL_VERIFICATION_INITIAL_STATE, ActionType as CancelVerificationActionType, update as cancelVerificationUpdate } from '@banking/ui/workflows/bank-accounts/cancel-verification';
import { INITIAL_STATE as CONFIRM_BANK_ACCOUNT_INITIAL_STATE, ActionType as ConfirmBankAccountActionType, update as confirmBankAccountUpdate } from '@banking/ui/workflows/bank-accounts/confirm-bank-account';
import { INITIAL_STATE as CHECKING_UPSELL_INITIAL_STATE, ActionType as CheckingUpsellActionType, update as checkingUpsellUpdate } from '@banking/ui/workflows/bank-accounts/checking-upsell';
import { RECOMMEND_CHECKING_VARIANT_CONTROL, RECOMMEND_CHECKING_VARIANT_UNIT_LINK } from '@banking/ui/utils/constants';
const DASHBOARD_PROJECT_ID = '16952400626';
const RECOMMEND_CHECKING_EXPERIMENT_NAME = 'ckg_1_0_dashboard_bank_accounts';
const EXPERIMENT_CDP_KEY = '23983470937';

// State
// =============================================================================

export const INITIAL_STATE = {
  allLocations: ALL_LOCATIONS_INITIAL_STATE,
  cancelVerificationModal: CANCEL_VERIFICATION_INITIAL_STATE,
  changeBankAccountModal: CHANGE_BANK_ACCOUNT_INITIAL_STATE,
  confirmBankAccountModal: CONFIRM_BANK_ACCOUNT_INITIAL_STATE,
  checkingUpsellModal: CHECKING_UPSELL_INITIAL_STATE,
  loading: true,
  externalBankAccounts: EXTERNAL_BANK_ACCOUNTS_INITIAL_STATE,
  merchantBankAccounts: [],
  merchantToken: '',
  recommendCheckingVariation: RECOMMEND_CHECKING_VARIANT_CONTROL,
  unitBankAccountGroups: {},
  unitAccounts: [],
  unitTokens: [],
  bankAccountDescription: '',
  isAccountOwner: true
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});

// Action
// =============================================================================

export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["InitBankAccounts"] = "WORKFLOW_INIT_BANK_ACCOUNTS";
  ActionType["GetBankAccountsReceived"] = "GET_BANK_ACCOUNTS_RECEIVED";
  ActionType["GetBankAccountsRejected"] = "GET_BANK_ACCOUNTS_REJECTED";
  ActionType["GetMerchantBankAccountsReceived"] = "GET_MERCHANT_BANK_ACCOUNTS_RECEIVED";
  ActionType["GetMerchantBankAccountsRejected"] = "GET_MERCHANT_BANK_ACCOUNTS_REJECTED";
  ActionType["GetRecommendCheckingExperimentRequestAction"] = "GET_RECOMMEND_CHECKING_EXPERIMENT_REQUEST";
  ActionType["GetRecommendCheckingExperimentResponseAction"] = "GET_RECOMMEND_CHECKING_EXPERIMENT_RESPONSE";
  ActionType["OpenCheckingUpsell"] = "OPEN_CHECKING_UPSELL";
  ActionType["ToggleLoading"] = "TOGGLE_LOADING";
  return ActionType;
}({});
// Action Creators
// =============================================================================

export const initBankAccountsAction = (merchantToken, unitTokens, unitAccounts, isAccountOwner) => ({
  type: ActionType.InitBankAccounts,
  merchantToken,
  unitTokens,
  unitAccounts,
  isAccountOwner
});
export const getRecommendCheckingExperimentRequestAction = () => ({
  type: ActionType.GetRecommendCheckingExperimentRequestAction
});
export const getRecommendCheckingExperimentResponseAction = response => ({
  type: ActionType.GetRecommendCheckingExperimentResponseAction,
  response
});
export const getBankAccountsReceivedAction = response => ({
  type: ActionType.GetBankAccountsReceived,
  response
});
export const getBankAccountsRejectedAction = error => ({
  type: ActionType.GetBankAccountsRejected,
  error
});
export const getMerchantBankAccountsReceivedAction = response => ({
  type: ActionType.GetMerchantBankAccountsReceived,
  response
});
export const getMerchantBankAccountsRejectedAction = error => ({
  type: ActionType.GetMerchantBankAccountsRejected,
  error
});
export const toggleLoadingAction = value => ({
  type: ActionType.ToggleLoading,
  value
});

// Update
// =============================================================================

function handleCancelVerificationActionType(context, state, action) {
  const [cancelVerificationModal, nextAction] = cancelVerificationUpdate(context)(state.cancelVerificationModal, action);
  if (cancelVerificationModal.isDone) {
    const {
      merchantToken,
      unitTokens,
      unitAccounts,
      isAccountOwner
    } = state;
    // If cancelling verification is successful, refresh data
    return [{
      ...state,
      cancelVerificationModal: CANCEL_VERIFICATION_INITIAL_STATE
    }, () => initBankAccountsAction(merchantToken, unitTokens, unitAccounts, isAccountOwner)];
  }
  return [{
    ...state,
    cancelVerificationModal
  }, nextAction];
}
function handleCheckingUpsellActionType(context, state, action) {
  const [checkingUpsellModal, nextAction] = checkingUpsellUpdate(context)(state.checkingUpsellModal, action);
  return [{
    ...state,
    checkingUpsellModal
  }, nextAction];
}
function handleConfirmBankAccountActionType(context, state, action) {
  const [confirmBankAccountModal, nextAction] = confirmBankAccountUpdate(context)(state.confirmBankAccountModal, action);
  if (confirmBankAccountModal.isDone) {
    const {
      merchantToken,
      unitTokens,
      unitAccounts,
      isAccountOwner
    } = state;
    return [{
      ...state,
      confirmBankAccountModal: CONFIRM_BANK_ACCOUNT_INITIAL_STATE
    }, () => initBankAccountsAction(merchantToken, unitTokens, unitAccounts, isAccountOwner)];
  }
  return [{
    ...state,
    confirmBankAccountModal
  }, nextAction];
}
function handleChangeBankAccountActionType(context, state, action) {
  const [changeBankAccountModal, nextAction] = changeBankAccountUpdate(context)(state.changeBankAccountModal, action);
  if (changeBankAccountModal.isDone) {
    const {
      merchantToken,
      unitTokens,
      unitAccounts,
      isAccountOwner
    } = state;
    // If cancelling verification is successful, refresh data
    return [{
      ...state,
      changeBankAccountModal: CHANGE_BANK_ACCOUNT_INITIAL_STATE
    }, () => initBankAccountsAction(merchantToken, unitTokens, unitAccounts, isAccountOwner)];
  }
  return [{
    ...state,
    changeBankAccountModal
  }, nextAction];
}
function handleExternalBankAccountsActionType(context, state, action) {
  const [externalBankAccounts, nextAction] = externalBankAccountsUpdate(context)(state.externalBankAccounts, action);
  return [{
    ...state,
    externalBankAccounts
  }, nextAction];
}
function handleAllLocationsActionType(context, state, action) {
  const [allLocations, nextAction] = allLocationsUpdate(context)(state.allLocations, action);
  if (allLocations.requiresRefresh) {
    const {
      merchantToken,
      unitTokens,
      unitAccounts,
      isAccountOwner
    } = state;
    return [{
      ...state,
      allLocations: {
        ...allLocations,
        requiresRefresh: false
      }
    }, () => initBankAccountsAction(merchantToken, unitTokens, unitAccounts, isAccountOwner)];
  }
  return [{
    ...state,
    allLocations
  }, nextAction];
}
function handleInitBankAccounts(context, state, action) {
  const {
    merchantToken,
    unitAccounts,
    unitTokens,
    isAccountOwner
  } = action;
  const bankAccountDescription = getHeadingDescription(context.intl);
  const work = () => {
    const getBankAccountsReceived = from(context.bankAccountApi.getBankAccounts(GetBankAccountsRequest.create({
      unitToken: [...unitTokens]
    })).then(getBankAccountsReceivedAction, getBankAccountsRejectedAction));
    const getMerchantBankAccountsReceived = from(context.bankAccountApi.getMerchantBankAccounts(GetMerchantBankAccountsRequest.create({
      merchantToken
    })).then(getMerchantBankAccountsReceivedAction, getMerchantBankAccountsRejectedAction));
    return concat(getBankAccountsReceived, getMerchantBankAccountsReceived, of(toggleLoadingAction(false)));
  };
  return [{
    ...state,
    merchantToken,
    unitAccounts,
    unitTokens,
    bankAccountDescription,
    isAccountOwner
  }, work];
}
function handleGetRecommendCheckingExperimentRequestAction(context, state) {
  const work = () => {
    const experimentGroup = context.depositsBatExperiment.isEligibleForCheckingUpsell(state.unitTokens[0]).then(isEligible => {
      if (isEligible) {
        return context.squareExperiments.getActivateExperiment({
          projectId: DASHBOARD_PROJECT_ID,
          experimentName: RECOMMEND_CHECKING_EXPERIMENT_NAME,
          token: state.merchantToken,
          cdpKey: EXPERIMENT_CDP_KEY
        }).catch(() => 'control').then(getRecommendCheckingExperimentResponseAction);
      } else {
        return null;
      }
    });
    return from(experimentGroup);
  };
  return [state, work];
}
function handleGetRecommendCheckingExperimentResponseAction(state, action) {
  const showUseSquareCheckingLink = action.response === RECOMMEND_CHECKING_VARIANT_UNIT_LINK;
  return [{
    ...state,
    allLocations: {
      ...state.allLocations,
      showUseSquareCheckingLink
    },
    recommendCheckingVariation: action.response
  }, null];
}
function handleGetBankAccountsReceived(state, action) {
  const {
    response
  } = action;
  const {
    merchantToken,
    unitAccounts,
    isAccountOwner
  } = state;
  const work = () => {
    return initAllLocationsAction(merchantToken, response.unitBankAccountGroups, unitAccounts, isAccountOwner);
  };
  return [{
    ...state,
    unitBankAccountGroups: response.unitBankAccountGroups
  }, work];
}
function handleGetBankAccountsRejected(context, state, action) {
  // TODO(jmah): add error screen
  context.sentry.captureException(action.error);
  return [state, null];
}
function handleGetMerchantBankAccountsRejected(context, state, action) {
  // TODO(jmah): add error screen
  context.sentry.captureException(action.error);
  return [state, null];
}
function handleGetMerchantBankAccountsReceived(state, action) {
  const {
    response
  } = action;
  const merchantBankAccounts = response.merchantBankAccounts;
  const work = () => {
    return initExternalBankAccountsAction(merchantBankAccounts);
  };
  return [{
    ...state,
    merchantBankAccounts
  }, work];
}
function handleToggleLoading(state, action) {
  const loading = action.value ?? !state.loading;
  return [{
    ...state,
    loading
  }, null];
}

// MISC
// =============================================================================

function getHeadingDescription(intl) {
  return intl.t('banking.unifiedBankAccounts.heading.description');
}
// This is disabled because the linter is complaining about the number of lines in the function but this is one switch case statement
/* eslint-disable complexity */
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.InitBankAccounts:
      return handleInitBankAccounts(context, state, action);
    case ActionType.GetRecommendCheckingExperimentRequestAction:
      return handleGetRecommendCheckingExperimentRequestAction(context, state);
    case ActionType.GetRecommendCheckingExperimentResponseAction:
      return handleGetRecommendCheckingExperimentResponseAction(state, action);
    case ActionType.GetBankAccountsReceived:
      return handleGetBankAccountsReceived(state, action);
    case ActionType.GetBankAccountsRejected:
      return handleGetBankAccountsRejected(context, state, action);
    case ActionType.GetMerchantBankAccountsReceived:
      return handleGetMerchantBankAccountsReceived(state, action);
    case ActionType.GetMerchantBankAccountsRejected:
      return handleGetMerchantBankAccountsRejected(context, state, action);
    case ActionType.ToggleLoading:
      return handleToggleLoading(state, action);
    case AllLocationsActionType.AddExternalBankAccountReceived:
    case AllLocationsActionType.InitAllLocations:
    case AllLocationsActionType.OpenLinkBankAccountForm:
    case AllLocationsActionType.ResendEmailReceived:
    case AllLocationsActionType.ResendEmailRejected:
    case AllLocationsActionType.UserClickAddExternalBankAccount:
    case AllLocationsActionType.UserClickCancelVerification:
    case AllLocationsActionType.UserClickChangeBankAccount:
    case AllLocationsActionType.UserClickNextPage:
    case AllLocationsActionType.UserClickPrevPage:
    case AllLocationsActionType.UserClickResendEmail:
    case AllLocationsActionType.UserClickUseChecking:
      return handleAllLocationsActionType(context, state, action);
    case ChangeBankAccountActionType.IncrementScreen:
    case ChangeBankAccountActionType.ToggleChangeBankAccountModal:
    case ChangeBankAccountActionType.UpdateBankAccountReceived:
    case ChangeBankAccountActionType.UpdateBankAccountRejected:
    case ChangeBankAccountActionType.UserClickUpdateBankAccount:
      return handleChangeBankAccountActionType(context, state, action);
    case CancelVerificationActionType.InitCancelVerificationModal:
    case CancelVerificationActionType.CancelVerificationReceived:
    case CancelVerificationActionType.CancelVerificationRejected:
    case CancelVerificationActionType.ToggleCancelVerificationModal:
    case CancelVerificationActionType.UserClickCancelBankVerification:
    case CancelVerificationActionType.UserClickContinueBankVerification:
    case CancelVerificationActionType.WorkflowSetDone:
      return handleCancelVerificationActionType(context, state, action);
    case CheckingUpsellActionType.OpenCheckingUpsellModal:
    case CheckingUpsellActionType.CloseCheckingUpsellModal:
    case CheckingUpsellActionType.GetInstantTransferFeeReceived:
      return handleCheckingUpsellActionType(context, state, action);
    case ConfirmBankAccountActionType.ConfirmBankAccountRejected:
    case ConfirmBankAccountActionType.ConfirmBankAccountReceived:
    case ConfirmBankAccountActionType.IncrementScreen:
    case ConfirmBankAccountActionType.InitConfirmBankAccount:
    case ConfirmBankAccountActionType.ToggleConfirmBankAccountModal:
    case ConfirmBankAccountActionType.CleanupQueryParams:
      return handleConfirmBankAccountActionType(context, state, action);
    case ExternalBankAccountsActionType.InitExternalBankAccounts:
    case ExternalBankAccountsActionType.UserClickNextPage:
    case ExternalBankAccountsActionType.UserClickPrevPage:
      return handleExternalBankAccountsActionType(context, state, action);
  }
};
/* eslint-enable complexity */

const bankAccounts = (seed, context) => {
  const w = workflow(update(context), seed);
  return {
    states: w.states,
    updates: w.updates,
    emit: w.emit
  };
};
export default bankAccounts;